import React, { useEffect, useMemo, useState } from "react";

import { faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Accordion,
  Button,
  Col,
  Container,
  Image,
  Modal,
  Row,
} from "react-bootstrap";
import Gallery from "react-image-gallery";

import useAwards from "@/hooks/useAwards";
import { getCurrentLang } from "@/utils/Local";

import i18n from "../i18n";
import ErrorComponent from "./Error.component";
import renderItem from "./gallery/RenderItem";
import LoaderComponent from "./Loader.component";

import type { IAward, IAwardImage } from "@/interfaces/IAward";
import type { IGalleryImage } from "@/interfaces/IGalleryImage";

const AwardsComponent = () => {
  const { t } = i18n;
  const lang = getCurrentLang();
  const [showGallery, setShowGallery] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  // always show the first 5 awards
  const [activeKeys, setActiveKeys] = useState<string[]>([
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
  ]);
  const [galleryImages, setGalleryImages] = useState<IGalleryImage[]>([]);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useAwards();

  const awards = useMemo(
    () => data?.pages.flatMap((page) => page.awards) ?? [],
    [data?.pages],
  );

  const openGallery = (categoryIndex: number, imgIndex: number) => {
    // load images
    const images: IGalleryImage[] = [];
    awards[categoryIndex].images.map((img, index) => {
      images.push({
        original: img.original,
        thumbnail: img.thumbnail,
        description: `${awards[categoryIndex].title[lang]} (${index + 1})`,
        alt: `${awards[categoryIndex].title[lang]} (${index + 1})`,
        isVideo: img.isVideo ?? false,
      });
      return null;
    });
    setGalleryImages(images);
    setCurrentIndex(imgIndex);
    setShowGallery(true);
  };

  // Toggle accordion expansion
  const handleToggle = (key: string) => {
    setActiveKeys(
      (prevKeys) =>
        prevKeys.includes(key)
          ? prevKeys.filter((k) => k !== key) // Remove key to collapse
          : [...prevKeys, key], // Add key to expand
    );
  };

  useEffect(() => {
    if (data?.pages.length) {
      const latestPage = data.pages[data.pages.length - 1];
      const latestItemKey = awards.length - latestPage.awards.length;
      setActiveKeys((prevKeys) => [...prevKeys, latestItemKey.toString()]); // Expand the latest item
    }
  }, [data?.pages, awards]);

  if (isFetching) {
    return <LoaderComponent standalone={false} />;
  }

  if (error) {
    return <ErrorComponent standalone={false} />;
  }

  if (!data) {
    return <React.Fragment>l</React.Fragment>;
  }

  return (
    <React.Fragment>
      <Modal
        fullscreen
        className="modalGallery"
        data-bs-theme="dark"
        onHide={() => setShowGallery(false)}
        show={showGallery}
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <Gallery
            items={galleryImages}
            renderItem={(item) => renderItem(item as IGalleryImage)}
            showThumbnails={false}
            startIndex={currentIndex}
          />
        </Modal.Body>
      </Modal>

      <Accordion alwaysOpen activeKey={activeKeys}>
        {awards.map((award: IAward, index: number) => (
          <Accordion.Item key={index} eventKey={index.toString()}>
            <Accordion.Header onClick={() => handleToggle(index.toString())}>
              {award.title[lang]}
              {award?.link && award.link[lang] ? (
                <a href={award.link[lang]} rel="noreferrer" target="_blank">
                  {" "}
                  <FontAwesomeIcon className="ms-4" icon={faExternalLink} />
                </a>
              ) : null}
            </Accordion.Header>
            <Accordion.Body>
              <Container>
                <Row>
                  {award.images.map((image: IAwardImage, imgIndex: number) => (
                    <Col
                      key={imgIndex}
                      className="mb-4 position-relative text-center"
                      md={4}
                      xs={6}
                    >
                      <Image
                        alt={`${award.title[lang]} (${imgIndex + 1})`}
                        className="mw-100"
                        loading="lazy"
                        onClick={() => openGallery(index, imgIndex)}
                        role="button"
                        src={image.thumbnail}
                        style={{ objectFit: "cover" }}
                      />
                    </Col>
                  ))}
                </Row>
              </Container>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      {hasNextPage ? (
        <Row>
          <Col className="text-center mt-4" xs={12}>
            <Button
              className="btn-outline"
              disabled={isFetchingNextPage}
              onClick={async () => fetchNextPage().then(() => {})}
              size="sm"
              variant="outline-success ps-4 pe-4"
            >
              {t("load_more")}
            </Button>
          </Col>
        </Row>
      ) : null}
    </React.Fragment>
  );
};

export default AwardsComponent;
